import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const isHome = location.pathname === '/';
  const isSpreadsheet = location.pathname === '/kakobuy-spreadsheet';
  const isGuides = location.pathname === '/kakobuy-spreadsheet-guide' || location.pathname === '/kakobuy-spreadsheet-how-to-use';
  const isSafety = location.pathname === '/safety';
  const isAbout = location.pathname === '/about';

  const linkClass = (active: boolean) =>
    `text-sm font-medium transition-colors whitespace-nowrap ${
      active ? 'text-primary-600' : 'text-foreground-600 hover:text-primary-600'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-[72px]">
          {isHome ? (
            <h1 className="contents">
              <Link to="/" className="flex items-center gap-2 group">
                <span className="text-xl font-heading font-bold text-primary-600 whitespace-nowrap">
                  Kakobuy Finds
                </span>
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
              </Link>
            </h1>
          ) : (
            <Link to="/" className="flex items-center gap-2 group">
              <span className="text-xl font-heading font-bold text-primary-600 whitespace-nowrap">
                Kakobuy Finds
              </span>
              <span className="w-2 h-2 rounded-full bg-accent-500"></span>
            </Link>
          )}

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={linkClass(isHome)}
            >
              Home
            </Link>

            <Link
              to="/kakobuy-spreadsheet"
              className={linkClass(isSpreadsheet)}
            >
              Spreadsheet
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center px-4 py-3 text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 transition-all hover:pl-5 cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 mr-3 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/kakobuy-spreadsheet-guide"
              className={linkClass(isGuides)}
            >
              Guides
            </Link>

            <Link
              to="/safety"
              className={linkClass(isSafety)}
            >
              Safety
            </Link>

            <Link
              to="/about"
              className={linkClass(isAbout)}
            >
              About
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 h-11 flex items-center text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 h-11 flex items-center text-sm font-semibold border-2 border-primary-500 text-primary-600 rounded-full hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="px-5 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className="ri-home-4-line text-base"></i>
              </span>
              Home
            </Link>

            <Link
              to="/kakobuy-spreadsheet"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className="ri-database-2-line text-base"></i>
              </span>
              Spreadsheet
            </Link>

            <div>
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full gap-3 py-3 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                    <i className="ri-grid-line text-base"></i>
                  </span>
                  Categories
                </span>
                <span className="flex items-center gap-1 text-xs text-foreground-400">
                  <span className="px-1.5 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[10px] font-medium">{categories.length}</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 text-base ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  mobileCatOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="ml-11 mt-1 mb-2 border-l-2 border-background-200 pl-4 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 py-2.5 text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50/50 rounded-md px-2 -ml-2 transition-all cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-300 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="my-2 border-t border-background-100"></div>

            <Link
              to="/kakobuy-spreadsheet-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Guides
            </Link>

            <Link
              to="/safety"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                <i className="ri-shield-check-line text-base"></i>
              </span>
              Safety
            </Link>

            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-100 text-foreground-500">
                <i className="ri-information-line text-base"></i>
              </span>
              About
            </Link>

            <div className="pt-4 pb-2 flex flex-col gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all"
              >
                <i className="ri-external-link-line text-xs"></i>
                Kakobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full py-3.5 text-sm font-semibold border-2 border-primary-500 text-primary-600 rounded-full hover:bg-primary-50 transition-all"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}